import moment, { MomentInput, Moment as MomentTypes } from 'moment-timezone';
import { LOCALE_DATE_FORMAT, SERVER_DATETIME_FORMAT } from './constants';

// Prefer native browser API for accuracy, fallback to moment's guess if native browser API is not available.
export const getUserTimezone = () =>
    Intl.DateTimeFormat().resolvedOptions().timeZone || moment.tz.guess();

export const convertToDate = (date?: Date | string | null) => (date ? new Date(date) : null);

export const formatDate = (
    date: MomentInput,
    format = LOCALE_DATE_FORMAT,
    create = false,
    timezone = '',
) => {
    if (!date && !create) return '';
    if (create) return moment().format(format);
    if (timezone) return moment.utc(date).tz(timezone).format(format);
    return moment.utc(date).tz(getUserTimezone()).format(format);
};

export const prepareDateAsURLParam = (date: MomentInput) => {
    if (!date) return '';
    const formattedDate = moment.utc(date).tz(getUserTimezone()).toISOString();
    return encodeURIComponent(formattedDate);
};

export const getDate = (
    date: MomentInput = undefined,
    timezone: string | undefined | null = undefined,
) => {
    if (!date) return moment.utc().tz(timezone || getUserTimezone());
    return moment.utc(date).tz(timezone || getUserTimezone());
};

// Get startDate and endDate converted to shop time zone for Total sales data
export const getTotalSalesDateRangeUTC = (
    date: MomentInput,
    type: 'start' | 'end',
    timezone = '',
) => {
    if (!date) return moment.utc();

    if (type === 'start') {
        return moment.tz(moment(date).format('YYYY-MM-DD'), timezone).startOf('day').utc();
    }
    return moment.tz(moment(date).format('YYYY-MM-DD'), timezone).endOf('day').utc();
};

export const getAgeFromBirthdate = (birthdate: MomentInput) => {
    if (!birthdate) return '';
    return moment.utc().diff(moment.utc(birthdate), 'years');
};

export const parseLocalDate = (dateString: string) => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
};

export const decomposeDate = (date: Date) => {
    return {
        year: date.getFullYear(),
        month: date.getMonth(),
        day: date.getDate(),
    };
};

export const constructDateString = (timestamp: string | number) => {
    const d = new Date(timestamp);
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(
        d.getUTCDate(),
    ).padStart(2, '0')}`;
};

export const convertEpochDate = (date: string | undefined, format = SERVER_DATETIME_FORMAT) => {
    if (!date) return '';
    return moment.utc(parseInt(date, 10)).tz(getUserTimezone()).format(format);
};

export const calculateFutureDate = (afterDays: number) => {
    return new Date(new Date().getTime() + afterDays * 24 * 60 * 60 * 1000).toLocaleDateString(
        'en-US',
        {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        },
    );
};

export const isValidIANATimezone = (timeZone: string): boolean => moment.tz.zone(timeZone) !== null;

// ** This function accepts endDateString as an ISO String
export const isDateExpired = (endDateString: string): boolean =>
    moment.utc().isAfter(moment.utc(endDateString));

export const convertUtcToClientTime = (
    utcDateValue: string | Date,
    timezone: string | undefined | null = undefined,
) => {
    if (!utcDateValue) return '';
    const date = moment.utc(utcDateValue);
    if (timezone && isValidIANATimezone(timezone)) {
        return date.tz(timezone).format(LOCALE_DATE_FORMAT);
    }
    return date.tz(getUserTimezone()).format('YYYY-MM-DD HH:mm:ss');
};

export const formatIfDate = (params: { value: string | null | undefined; format?: string }) => {
    const { value, format = 'MMM D, YYYY' } = params;
    const date = moment(value);
    return date.isValid() ? date.format(format) : 'N/A';
};

export type Moment = MomentTypes;

export default moment;
